import { App, Modal } from 'antd';
import type { ModalFuncProps } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons';

// antd's STATIC Modal.confirm() renders into its own React root, so it never sees
// the app's <ConfigProvider> — meaning no RTL, no Ploni font, no brand colours,
// no heIL locale. The result looks like a default LTR antd dialog, NOT the Arbox
// confirm. We instead route through the <App> modal instance (App.useApp().modal),
// which DOES inherit the surrounding provider. ConfirmModalHost is mounted once
// inside <App> to capture that instance; confirmModal() then uses it.
type ModalApi = ReturnType<typeof App.useApp>['modal'];
let modalApi: ModalApi | null = null;

/** Mount once inside antd's <App> (which is inside <ConfigProvider>). Captures
 *  the context-aware modal instance so the imperative confirmModal() below can
 *  be called from anywhere (event handlers, not just React render). */
export function ConfirmModalHost() {
  const { modal } = App.useApp();
  modalApi = modal;
  return null;
}

// Arbox-style confirmation dialog — matches the confirm modals in the Arbox
// webapp so ours feel native inside the iframe: a titled header, plain body
// text (NO antd status icon), a light grey close ✕ in the corner, and a
// ביטול / אישור footer. Drop-in replacement for Modal.confirm — pass title,
// content, onOk, and optionally okText.
export function confirmModal(props: ModalFuncProps) {
  const merged: ModalFuncProps = {
    icon: null,
    closable: true,
    closeIcon: <FontAwesomeIcon icon={faXmark} style={{ fontSize: 18, color: '#9D9D9D' }} />,
    okText: 'אישור',
    cancelText: 'ביטול',
    ...props,
  };
  // Prefer the themed/RTL instance; fall back to the static one only if the host
  // hasn't mounted yet (shouldn't happen in practice).
  return (modalApi ?? Modal).confirm(merged);
}
